import { useState } from 'react'

import { GdalLayerInfo } from '@app/modules/Home/Data/types'
import { extractGdalInfo } from '@app/modules/Home/Data/utils/gdal.utils'
import { groupFiles } from '@app/modules/Home/Data/utils/fileGroup.utils'

export const MAX_GDAL_SIZE = 2.5 * 1024 ** 3

export const useGdalExtract = () => {
  const [gdalInfo, setGdalInfo] = useState<GdalLayerInfo | null>(null)
  const [gdalFailed, setGdalFailed] = useState(false)
  const [gdalSkipped, setGdalSkipped] = useState(false)
  const [extracting, setExtracting] = useState(false)

  const resetResults = () => {
    setGdalInfo(null)
    setGdalFailed(false)
    setGdalSkipped(false)
  }

  const reset = () => {
    resetResults()
    setExtracting(false)
  }

  // Standalone extraction — used when re-extracting from a file input on the edit page.
  const extract = async (
    files: File[],
    inputRef?: React.MutableRefObject<HTMLInputElement | null>,
  ) => {
    const { primary, allFiles } = groupFiles(files)
    resetResults()

    if (primary.size > MAX_GDAL_SIZE) {
      setGdalSkipped(true)
      return
    }

    setExtracting(true)
    try {
      const result = await extractGdalInfo(allFiles)
      setGdalInfo(result)
    } catch {
      setGdalFailed(true)
    } finally {
      setExtracting(false)
      if (inputRef?.current) inputRef.current.value = ''
    }
  }

  // Prepares state before a parallel upload+extract via Promise.allSettled.
  const prepareParallel = (fileTooLarge: boolean) => {
    resetResults()
    if (!fileTooLarge) setExtracting(true)
  }

  // Applies the settled GDAL result after Promise.allSettled completes.
  // Callers are responsible for Sentry/logging on rejection before calling this.
  const applySettledResult = (
    fileTooLarge: boolean,
    result: PromiseSettledResult<GdalLayerInfo | null>,
  ) => {
    setExtracting(false)
    if (fileTooLarge) {
      setGdalSkipped(true)
    } else if (result.status === 'fulfilled' && result.value !== null) {
      setGdalInfo(result.value)
    } else if (result.status === 'rejected') {
      setGdalFailed(true)
    }
  }

  return {
    gdalInfo,
    gdalFailed,
    gdalSkipped,
    extracting,
    extract,
    prepareParallel,
    applySettledResult,
    reset,
  }
}
